import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, BookOpen, CheckCircle } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <>
        <SeoHead title="Reset Password — AI Ghostwriter Pro" noindex={true} />
        <section className="min-h-screen flex items-center justify-center px-4 pt-20">
          <div className="text-center">
            <p className="text-red-400 mb-4">Invalid reset link. Please request a new one.</p>
            <Link to="/login" className="text-primary hover:underline text-sm">Back to Login</Link>
          </div>
        </section>
      </>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed. Link may have expired.');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <>
        <SeoHead title="Password Reset — AI Ghostwriter Pro" noindex={true} />
        <section className="min-h-screen flex items-center justify-center px-4 pt-20">
          <div className="w-full max-w-md text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h1 className="font-serif text-2xl tracking-tight mb-2">Password Reset</h1>
            <p className="text-sm text-muted-foreground mb-6">Your password has been updated successfully.</p>
            <Link to="/login" className="inline-block rounded-sm px-6 py-3 bg-primary text-primary-foreground font-semibold text-sm" data-testid="reset-to-login">
              Sign In
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SeoHead title="Reset Password — AI Ghostwriter Pro" noindex={true} />
      <section className="min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <BookOpen className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="font-serif text-xl text-foreground">AI Ghostwriter <span className="text-primary">Pro</span></span>
            </Link>
            <h1 className="font-serif text-2xl sm:text-3xl tracking-tight mb-2">New Password</h1>
            <p className="text-sm text-muted-foreground">Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            {error && <p className="text-sm text-red-400 text-center" data-testid="reset-error">{error}</p>}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type={showPass ? 'text' : 'password'} required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="New password (min 6 chars)" data-testid="reset-password"
                className="w-full pl-10 pr-10 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="reset-toggle-pass">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type={showPass ? 'text' : 'password'} required minLength={6} value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Confirm new password" data-testid="reset-confirm"
                className="w-full pl-10 pr-4 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
